import React, { useEffect, useRef, useState } from "react";
import ReactTooltip, { TooltipProps } from "react-tooltip";

import classnames from "classnames";
import { v4 as uuidv4 } from "uuid";

import "./Tooltips.styles.scss";

interface TooltipSpanProps extends TooltipProps {
  children: string;
  className?: string;
  tooltipClassName?: string;
  onClick?: () => void | Promise<void>;
}

export const TooltipSpan: React.FC<TooltipSpanProps> = ({
  children,
  className = "",
  onClick = () => null,
  delayShow = 250,
  tooltipClassName = "tooltip",
  place = "top",
  effect = "solid",
  type = "light",
  offset,
  ...props
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const id = uuidv4();
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  const checkTruncation = () => {
    if (spanRef.current) {
      const isOverflowing = spanRef.current.scrollWidth > spanRef.current.offsetWidth;
      setIsTruncated(isOverflowing);
    }
  };

  useEffect(() => {
    checkTruncation();

    const handleResize = () => {
      checkTruncation();
    };

    window.addEventListener("resize", handleResize);

    // Use ResizeObserver for more accurate detection
    let resizeObserver: ResizeObserver | null = null;
    if (spanRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        checkTruncation();
      });
      resizeObserver.observe(spanRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [children]);

  return (
    <>
      <span
        ref={spanRef}
        className={classnames(className, "tooltip-span")}
        onClick={onClick}
        data-tip={isTruncated ? children : ""}
        data-for={id}
        {...props}
      >
        {children}
      </span>
      <ReactTooltip
        delayShow={delayShow}
        id={id}
        className={tooltipClassName}
        place={place}
        effect={effect}
        type={type}
        offset={offset}
        {...props}
      />
    </>
  );
};
